import { Box } from '@mui/joy';
import { LockSimple } from '@phosphor-icons/react';
import { useState } from 'react';
import { achievementAppearance } from '../../../data/achievements';

/**
 * Значок достижения: картинка с сервера, а если её нет — иконка по коду.
 *
 * Картинки готовятся не для всех достижений сразу, поэтому оба состояния
 * равноправны и должны выглядеть одинаково уместно: есть `image` — показываем
 * его во всю плашку, нет — рисуем иконку на цветной подложке.
 *
 * Сюда же сведён вид «закрыто»: замок вместо иконки и приглушённый фон. Иначе
 * это пришлось бы повторять в карточке и в витрине вокруг аватара, а разойдясь,
 * они выглядели бы как два разных достижения.
 *
 * @param {string} code - код достижения из каталога
 * @param {string|null} [image] - путь с сервера (`/uploads/...`) или абсолютный URL
 * @param {number} size - сторона значка в пикселях
 * @param {boolean} [locked] - достижение ещё не получено
 */
export function AchievementBadge({ code, image, size, locked = false, sx = {} }) {
    const { icon: Icon, color } = achievementAppearance(code);
    // Битую ссылку не показываем пустым квадратом: откатываемся на иконку —
    // тот же вид, что и у достижения, которому картинку ещё не нарисовали.
    const [failed, setFailed] = useState(false);

    const url = image
        ? (image.startsWith('http') ? image : `${process.env.REACT_APP_API_URL}${image}`)
        : null;
    const showImage = Boolean(url) && !failed && !locked;

    const box = {
        width: size,
        height: size,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    };

    if (showImage) {
        return (
            <Box sx={{ ...box, ...sx }}>
                <Box
                    component="img"
                    src={url}
                    alt=""
                    onError={() => setFailed(true)}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                ...box,
                borderRadius: '50%',
                backgroundColor: locked ? 'neutral.softBg' : color,
                ...sx,
            }}
        >
            {locked ? (
                <LockSimple
                    size={Math.round(size * 0.55)}
                    weight="fill"
                    color="var(--joy-palette-text-tertiary)"
                />
            ) : (
                // Иконка занимает чуть больше половины плашки — с картинкой,
                // которая заполняет её целиком, это смотрится соразмерно.
                <Icon size={Math.round(size * 0.55)} weight="fill" color="#ffffff" />
            )}
        </Box>
    );
}

export default AchievementBadge;
